import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Flame, Star, ChevronRight, ChevronLeft } from "lucide-react";
import { springPresets } from "@/lib/motion";
import { ROUTE_PATHS } from "@/lib/index";
import { InactiveGraveWrapper, LAST_LOGIN_DATE } from "@/components/GraveEffects";
import { supabase } from "@/integrations/supabase/client";

/* ─── typy ─────────────────────────────────────────────── */
type GraveStyle =
  | "gothic" | "orthodox" | "classic" | "modern"
  | "obelisk" | "cross_celtic" | "double" | "child" | "military";

interface GraveEntry {
  id: string;
  name: string;
  years: string;
  epitaph: string;
  plan: "free" | "podstawowy" | "premium";
  lit: boolean;
  row: number;  // 0–19 (aleja)
  col: number;  // 0–4 (miejsce w alei)
  style: GraveStyle;
}

/* ─── Nazwy alei ─────────────────────────────────────────── */
const ALLEY_NAMES: Record<number, string> = {
  0:  "Aleja św. Piotra",
  1:  "Aleja Wiecznej Pamięci",
  2:  "Aleja Różana",
  3:  "Aleja Spokoju",
  4:  "Aleja Wiary i Nadziei",
  5:  "Aleja Matki Boskiej",
  6:  "Aleja Srebrna",
  7:  "Aleja Świętych",
  8:  "Aleja Złotego Liścia",
  9:  "Aleja Zmierzchu",
  10: "Aleja Ciszy",
  11: "Aleja Anioła Stróża",
  12: "Aleja Gwiazd",
  13: "Aleja Modlitwy",
  14: "Aleja Wiekuistego Światła",
  15: "Aleja Białej Róży",
  16: "Aleja Kwitnącej Magnolii",
  17: "Aleja Jesienna",
  18: "Aleja Wschodzącego Słońca",
  19: "Aleja Wieczności",
};

/* ─── style nagrobków ──────────────────────────────────── */
export const GRAVE_STYLES: { id: GraveStyle; label: string; desc: string; premium?: boolean }[] = [
  { id: "classic",      label: "Klasyczny",   desc: "Zaokrąglony wierzchołek, tradycyjny krzyż" },
  { id: "gothic",       label: "Gotycki",      desc: "Obelisk z ostrołukowym szczytem i ornamentami" },
  { id: "orthodox",     label: "Prawosławny",  desc: "Trójramienny krzyż ze wschodnią ikonografią" },
  { id: "modern",       label: "Nowoczesny",   desc: "Minimalistyczna pozioma płyta", premium: true },
  { id: "obelisk",      label: "Obelisk",      desc: "Wysoki, smukły monument", premium: true },
  { id: "cross_celtic", label: "Celtycki",     desc: "Krzyż z kołem i plecionką", premium: true },
  { id: "double",       label: "Podwójny",     desc: "Miejsce dla dwóch osób", premium: true },
  { id: "child",        label: "Aniołek",      desc: "Mały, delikatny monument dla dziecka" },
  { id: "military",     label: "Wojskowy",     desc: "Surowy, honorowy styl" },
];

/* ─── dane ────────────────────────────────────────────── */
// Zajęte miejsca (row, col) – losowo rozrzucone po całym placu
const OCCUPIED: GraveEntry[] = [
  { id:"g1",  name:"Jan Kowalski",        years:"1942–2019", epitaph:"Ukochany ojciec i dziadek",       plan:"premium",    lit:true,  row:0,  col:2, style:"gothic"      },
  { id:"g2",  name:"Maria Nowak",         years:"1955–2023", epitaph:"Zawsze w naszych sercach",        plan:"podstawowy", lit:false, row:0,  col:4, style:"classic"     },
  { id:"g3",  name:"Piotr Wiśniewski",    years:"1938–2015", epitaph:"Spokój duszy",                    plan:"free",       lit:true,  row:1,  col:1, style:"orthodox"    },
  { id:"g4",  name:"Anna Zielińska",      years:"1961–2021", epitaph:"Miłość trwa wiecznie",            plan:"premium",    lit:true,  row:2,  col:3, style:"obelisk"     },
  { id:"g5",  name:"Tomasz Lewandowski",  years:"1975–2022", epitaph:"Wspomnienia pozostają",           plan:"podstawowy", lit:false, row:3,  col:0, style:"modern"      },
  { id:"g6",  name:"Zofia Kamińska",      years:"1930–2010", epitaph:"Babcia zawsze blisko",            plan:"free",       lit:false, row:4,  col:2, style:"classic"     },
  { id:"g7",  name:"Krzysztof Wójcik",    years:"1968–2020", epitaph:"Odszedł za wcześnie",            plan:"premium",    lit:true,  row:5,  col:4, style:"cross_celtic" },
  { id:"g8",  name:"Elżbieta Maj",        years:"1952–2018", epitaph:"Niech ziemia lekką będzie",       plan:"podstawowy", lit:false, row:6,  col:1, style:"classic"     },
  { id:"g9",  name:"Stanisław Ptak",      years:"1945–2016", epitaph:"Tato, pamiętamy",                 plan:"free",       lit:true,  row:7,  col:3, style:"military"    },
  { id:"g10", name:"Helena Sikora",       years:"1958–2024", epitaph:"Światłość wiekuista",             plan:"podstawowy", lit:false, row:8,  col:0, style:"gothic"      },
  { id:"g11", name:"Andrzej Kubiak",      years:"1970–2023", epitaph:"Brat i przyjaciel",               plan:"premium",    lit:true,  row:9,  col:2, style:"obelisk"     },
  { id:"g12", name:"Jadwiga Bąk",         years:"1936–2012", epitaph:"Pokój wieczny",                   plan:"free",       lit:false, row:10, col:4, style:"child"       },
  { id:"g13", name:"Marek Szymański",     years:"1963–2019", epitaph:"Żyje w pamięci bliskich",         plan:"podstawowy", lit:true,  row:11, col:1, style:"orthodox"    },
  { id:"g14", name:"Irena Walczak",       years:"1948–2017", epitaph:"Zawsze w naszych myślach",        plan:"premium",    lit:true,  row:12, col:3, style:"double"      },
  { id:"g15", name:"Ryszard Dąbrowski",   years:"1955–2021", epitaph:"Dobry człowiek",                  plan:"free",       lit:false, row:13, col:0, style:"classic"     },
  { id:"g16", name:"Wanda Kaczmarek",     years:"1940–2014", epitaph:"Mama na zawsze",                  plan:"podstawowy", lit:false, row:14, col:2, style:"modern"      },
  { id:"g17", name:"Józef Przybysz",      years:"1932–2008", epitaph:"Cicha modlitwa",                  plan:"free",       lit:true,  row:15, col:4, style:"gothic"      },
  { id:"g18", name:"Katarzyna Michalska", years:"1980–2022", epitaph:"Odeszła za młodo",                plan:"premium",    lit:true,  row:16, col:1, style:"cross_celtic" },
  { id:"g19", name:"Lech Grabowski",      years:"1950–2020", epitaph:"W sercu rodziny",                 plan:"podstawowy", lit:false, row:17, col:3, style:"military"    },
  { id:"g20", name:"Bożena Nowacka",      years:"1965–2023", epitaph:"Wspomnienie jak kwiat",           plan:"free",       lit:false, row:18, col:0, style:"child"       },
];

// Zestaw zajętych pozycji dla szybkiego sprawdzania
const OCCUPIED_SET = new Set(OCCUPIED.map(g => `${g.row}-${g.col}`));

// Generuj pełną siatkę 20×5 = 100 miejsc
const GRAVES: GraveEntry[] = [];
for (let row = 0; row < 20; row++) {
  for (let col = 0; col < 5; col++) {
    const key = `${row}-${col}`;
    const found = OCCUPIED.find(g => g.row === row && g.col === col);
    if (found) {
      GRAVES.push(found);
    } else {
      GRAVES.push({
        id: `empty-${row}-${col}`,
        name: "", years: "", epitaph: "",
        plan: "free", lit: false,
        row, col,
        style: "classic",
      });
    }
  }
}

const PLAN_COLORS: Record<string, string> = {
  free:       "oklch(0.50 0.02 220)",
  podstawowy: "oklch(0.65 0.10 200)",
  premium:    "oklch(0.72 0.14 45)",
};

/* ═══════════════════════════════════════════════════════════
   SVG-kształty nagrobków
══════════════════════════════════════════════════════════════ */
function stoneColor(plan: string, highlight: boolean) {
  if (highlight) {
    if (plan === "premium")    return { fill: "#2a2510", stroke: "rgba(212,175,55,0.9)" };
    if (plan === "podstawowy") return { fill: "#0d1a22", stroke: "rgba(100,180,220,0.8)" };
    return { fill: "#111820", stroke: "rgba(140,160,190,0.6)" };
  }
  if (plan === "premium")    return { fill: "#1c1a12", stroke: "rgba(212,175,55,0.45)" };
  if (plan === "podstawowy") return { fill: "#0b1319", stroke: "rgba(80,140,175,0.4)" };
  return { fill: "#0e1318", stroke: "rgba(80,95,115,0.35)" };
}

function ShapeClassic({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 56 82" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M4 82V30 Q4 4 28 4 Q52 4 52 30 V82 Z" fill={fill} stroke={stroke} strokeWidth="1.2"/>
      <line x1="12" y1="60" x2="44" y2="60" stroke={stroke} strokeWidth="0.6" opacity="0.5"/>
      <line x1="14" y1="66" x2="42" y2="66" stroke={stroke} strokeWidth="0.4" opacity="0.3"/>
      <line x1="28" y1="22" x2="28" y2="44" stroke={stroke} strokeWidth="1.2" opacity="0.6"/>
      <line x1="18" y1="30" x2="38" y2="30" stroke={stroke} strokeWidth="1.2" opacity="0.6"/>
      <rect x="0" y="79" width="56" height="3" rx="1" fill={fill} stroke={stroke} strokeWidth="0.8"/>
    </svg>
  );
}
function ShapeGothic({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 52 88" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M4 88V36 L26 4 L48 36 V88 Z" fill={fill} stroke={stroke} strokeWidth="1.2"/>
      <path d="M18 40 L26 26 L34 40 Z" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.5"/>
      <line x1="26" y1="26" x2="26" y2="46" stroke={stroke} strokeWidth="0.6" opacity="0.4"/>
      <circle cx="26" cy="54" r="4" fill="none" stroke={stroke} strokeWidth="0.6" opacity="0.4"/>
      <line x1="14" y1="66" x2="38" y2="66" stroke={stroke} strokeWidth="0.5" opacity="0.35"/>
      <line x1="16" y1="72" x2="36" y2="72" stroke={stroke} strokeWidth="0.4" opacity="0.25"/>
      <rect x="0" y="85" width="52" height="3" rx="1" fill={fill} stroke={stroke} strokeWidth="0.8"/>
    </svg>
  );
}
function ShapeOrthodox({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 54 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M6 84 V26 Q6 6 27 6 Q48 6 48 26 V84 Z" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <line x1="27" y1="12" x2="27" y2="54" stroke={stroke} strokeWidth="1.4" opacity="0.7"/>
      <line x1="16" y1="24" x2="38" y2="24" stroke={stroke} strokeWidth="1.4" opacity="0.7"/>
      <line x1="19" y1="32" x2="35" y2="32" stroke={stroke} strokeWidth="1.0" opacity="0.6"/>
      <line x1="16" y1="46" x2="38" y2="40" stroke={stroke} strokeWidth="0.9" opacity="0.55"/>
      <line x1="12" y1="62" x2="42" y2="62" stroke={stroke} strokeWidth="0.5" opacity="0.35"/>
      <line x1="14" y1="68" x2="40" y2="68" stroke={stroke} strokeWidth="0.4" opacity="0.25"/>
      <rect x="2" y="81" width="50" height="3" rx="1" fill={fill} stroke={stroke} strokeWidth="0.8"/>
    </svg>
  );
}
function ShapeModern({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 72 52" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="2" y="2" width="68" height="42" rx="3" fill={fill} stroke={stroke} strokeWidth="1.2"/>
      <line x1="10" y1="18" x2="62" y2="18" stroke={stroke} strokeWidth="0.7" opacity="0.5"/>
      <rect x="28" y="8" width="16" height="8" rx="1" fill="none" stroke={stroke} strokeWidth="0.6" opacity="0.4"/>
      <line x1="12" y1="28" x2="60" y2="28" stroke={stroke} strokeWidth="0.5" opacity="0.35"/>
      <line x1="14" y1="34" x2="58" y2="34" stroke={stroke} strokeWidth="0.4" opacity="0.25"/>
      <rect x="0" y="44" width="72" height="6" rx="2" fill={fill} stroke={stroke} strokeWidth="0.8"/>
    </svg>
  );
}
function ShapeObelisk({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 36 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M18 2 L30 20 L28 96 L8 96 L6 20 Z" fill={fill} stroke={stroke} strokeWidth="1.2"/>
      <line x1="8" y1="25" x2="28" y2="25" stroke={stroke} strokeWidth="0.7" opacity="0.5"/>
      <line x1="9" y1="30" x2="27" y2="30" stroke={stroke} strokeWidth="0.4" opacity="0.3"/>
      <path d="M18 36 L22 44 L18 42 L14 44 Z" fill="none" stroke={stroke} strokeWidth="0.7" opacity="0.5"/>
      <line x1="10" y1="60" x2="26" y2="60" stroke={stroke} strokeWidth="0.5" opacity="0.35"/>
      <line x1="10" y1="66" x2="26" y2="66" stroke={stroke} strokeWidth="0.4" opacity="0.25"/>
      <rect x="2" y="93" width="32" height="4" rx="1" fill={fill} stroke={stroke} strokeWidth="0.8"/>
      <rect x="0" y="95" width="36" height="3" rx="1" fill={fill} stroke={stroke} strokeWidth="0.7"/>
    </svg>
  );
}
function ShapeCrossCeltic({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 60 90" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="24" y="40" width="12" height="44" rx="2" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <rect x="6" y="16" width="48" height="12" rx="2" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <rect x="20" y="6" width="20" height="42" rx="2" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <circle cx="30" cy="22" r="14" fill="none" stroke={stroke} strokeWidth="1.2" opacity="0.7"/>
      <circle cx="30" cy="22" r="10" fill="none" stroke={stroke} strokeWidth="0.5" opacity="0.35"/>
      <rect x="16" y="82" width="28" height="6" rx="2" fill={fill} stroke={stroke} strokeWidth="0.9"/>
    </svg>
  );
}
function ShapeDouble({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M2 76 V28 Q2 6 22 6 Q42 6 42 28 V76 Z" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <path d="M38 76 V28 Q38 6 58 6 Q78 6 78 28 V76 Z" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <line x1="22" y1="18" x2="22" y2="36" stroke={stroke} strokeWidth="1" opacity="0.55"/>
      <line x1="13" y1="25" x2="31" y2="25" stroke={stroke} strokeWidth="1" opacity="0.55"/>
      <line x1="58" y1="18" x2="58" y2="36" stroke={stroke} strokeWidth="1" opacity="0.55"/>
      <line x1="49" y1="25" x2="67" y2="25" stroke={stroke} strokeWidth="1" opacity="0.55"/>
      <rect x="0" y="73" width="80" height="5" rx="2" fill={fill} stroke={stroke} strokeWidth="0.9"/>
    </svg>
  );
}
function ShapeChild({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 52 72" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="26" cy="22" rx="20" ry="20" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <rect x="14" y="38" width="24" height="28" rx="2" fill={fill} stroke={stroke} strokeWidth="1.1"/>
      <circle cx="26" cy="12" r="5" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.55"/>
      <path d="M20 18 Q26 24 32 18" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.5"/>
      <path d="M16 16 Q10 12 12 8 Q18 10 20 16" fill="none" stroke={stroke} strokeWidth="0.7" opacity="0.4"/>
      <path d="M36 16 Q42 12 40 8 Q34 10 32 16" fill="none" stroke={stroke} strokeWidth="0.7" opacity="0.4"/>
      <line x1="18" y1="50" x2="34" y2="50" stroke={stroke} strokeWidth="0.5" opacity="0.35"/>
      <line x1="18" y1="56" x2="34" y2="56" stroke={stroke} strokeWidth="0.4" opacity="0.25"/>
      <rect x="8" y="64" width="36" height="6" rx="2" fill={fill} stroke={stroke} strokeWidth="0.9"/>
    </svg>
  );
}
function ShapeMilitary({ plan, highlight }: { plan: string; highlight: boolean }) {
  const { fill, stroke } = stoneColor(plan, highlight);
  return (
    <svg viewBox="0 0 56 82" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="4" y="8" width="48" height="68" rx="2" fill={fill} stroke={stroke} strokeWidth="1.2"/>
      <rect x="2" y="4" width="52" height="8" rx="1" fill={fill} stroke={stroke} strokeWidth="1"/>
      <polygon points="28,16 30,22 36,22 31,26 33,32 28,28 23,32 25,26 20,22 26,22"
        fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.6"/>
      <line x1="10" y1="42" x2="46" y2="42" stroke={stroke} strokeWidth="0.7" opacity="0.5"/>
      <line x1="10" y1="48" x2="46" y2="48" stroke={stroke} strokeWidth="0.6" opacity="0.4"/>
      <line x1="10" y1="54" x2="46" y2="54" stroke={stroke} strokeWidth="0.5" opacity="0.3"/>
      <line x1="10" y1="60" x2="46" y2="60" stroke={stroke} strokeWidth="0.5" opacity="0.25"/>
      <rect x="0" y="75" width="56" height="5" rx="1" fill={fill} stroke={stroke} strokeWidth="0.8"/>
    </svg>
  );
}
function GraveShape({ style, plan, highlight }: { style: GraveStyle; plan: string; highlight: boolean }) {
  const props = { plan, highlight };
  switch (style) {
    case "gothic":       return <ShapeGothic {...props} />;
    case "orthodox":     return <ShapeOrthodox {...props} />;
    case "modern":       return <ShapeModern {...props} />;
    case "obelisk":      return <ShapeObelisk {...props} />;
    case "cross_celtic": return <ShapeCrossCeltic {...props} />;
    case "double":       return <ShapeDouble {...props} />;
    case "child":        return <ShapeChild {...props} />;
    case "military":     return <ShapeMilitary {...props} />;
    default:             return <ShapeClassic {...props} />;
  }
}

/* ─── Pojedynczy nagrobek ──────────────────────────────── */
function GraveCard({ grave, onClick, highlight, plotNumber }: {
  grave: GraveEntry;
  onClick: (g: GraveEntry) => void;
  highlight: boolean;
  plotNumber: number;
}) {
  const isEmpty = !grave.name;
  const accentColor = PLAN_COLORS[grave.plan];

  const dims: Record<GraveStyle, { w: number; h: number }> = {
    classic:     { w: 56,  h: 82  },
    gothic:      { w: 52,  h: 88  },
    orthodox:    { w: 54,  h: 84  },
    modern:      { w: 72,  h: 52  },
    obelisk:     { w: 36,  h: 100 },
    cross_celtic:{ w: 60,  h: 90  },
    double:      { w: 80,  h: 80  },
    child:       { w: 52,  h: 72  },
    military:    { w: 56,  h: 82  },
  };
  const { w, h } = dims[grave.style];

  if (isEmpty) {
    return (
      <motion.div
        whileHover={{ scale: 1.06, y: -3 }}
        transition={springPresets.snappy}
        onClick={() => onClick(grave)}
        className="flex flex-col items-center gap-1 cursor-pointer group"
      >
        {/* numer miejsca */}
        <span className="text-[8px] tabular-nums font-mono mb-0.5 opacity-25 group-hover:opacity-60 transition-opacity"
          style={{ color: 'rgba(160,175,200,0.8)' }}>#{plotNumber}</span>
        <div
          className="flex items-center justify-center transition-all duration-300 rounded-sm"
          style={{ width: 56, height: 72, border: '1.5px dashed oklch(0.28 0.03 220)', background: 'oklch(0.08 0.012 230)' }}
        >
          <span className="text-2xl opacity-20 group-hover:opacity-50 text-primary transition-opacity">+</span>
        </div>
        <span className="text-[9px] uppercase tracking-widest opacity-20 group-hover:opacity-50 transition-opacity"
          style={{ color: 'rgba(180,195,215,0.7)' }}>wolne</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.07, y: -5 }}
      transition={springPresets.snappy}
      onClick={() => onClick(grave)}
      className="flex flex-col items-center gap-1 cursor-pointer group relative"
    >
      {/* numer miejsca */}
      <span className="text-[8px] tabular-nums font-mono opacity-40 group-hover:opacity-70 transition-opacity"
        style={{ color: 'rgba(180,195,215,0.8)' }}>#{plotNumber}</span>

      {/* Świeczka */}
      {grave.lit && (
        <motion.div
          animate={{ opacity: [0.6, 1, 0.7, 1, 0.8], scale: [1, 1.2, 0.9, 1.15, 1] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center z-10"
        >
          <div className="w-0.5 h-3 rounded-full"
            style={{ background: `linear-gradient(to top, ${accentColor}, oklch(0.88 0.18 78))` }} />
          <div className="w-2 h-2 rounded-full blur-[3px]"
            style={{ background: 'oklch(0.88 0.20 70 / 0.85)', marginTop: -3 }} />
        </motion.div>
      )}
      {!grave.lit && <div className="h-5" />}

      {/* Highlight glow */}
      {highlight && (
        <div className="absolute inset-0 rounded-sm pointer-events-none z-0"
          style={{ boxShadow: `0 0 24px ${accentColor}55`, borderRadius: 4 }} />
      )}

      {/* Oznaczenie planu */}
      <div className="flex items-center justify-center gap-0.5 h-3">
        {grave.plan === 'premium' && (
          <>
            <div className="w-1 h-1 rounded-full" style={{ background: accentColor, boxShadow: `0 0 4px ${accentColor}` }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor, boxShadow: `0 0 6px ${accentColor}` }} />
            <div className="w-1 h-1 rounded-full" style={{ background: accentColor, boxShadow: `0 0 4px ${accentColor}` }} />
          </>
        )}
        {grave.plan === 'podstawowy' && (
          <>
            <div className="w-1 h-1 rounded-full opacity-50" style={{ background: accentColor }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor, boxShadow: `0 0 5px ${accentColor}80` }} />
            <div className="w-1 h-1 rounded-full opacity-50" style={{ background: accentColor }} />
          </>
        )}
        {grave.plan === 'free' && (
          <div className="w-1 h-1 rounded-full opacity-40" style={{ background: accentColor }} />
        )}
      </div>

      {/* SVG nagrobek */}
      <div className="relative z-10" style={{ width: w, height: h }}>
        <GraveShape style={grave.style} plan={grave.plan} highlight={highlight} />
      </div>

      {/* Imię */}
      <span className="text-center leading-tight transition-colors duration-200 group-hover:text-primary"
        style={{ fontSize: '9px', color: 'rgba(180,195,215,0.75)', maxWidth: `${Math.max(w, 56)}px` }}>
        {grave.name.split(" ").map((word, i) => (
          <span key={i} className="block">{word}</span>
        ))}
      </span>
    </motion.div>
  );
}

/* ─── Panel szczegółów ──────────────────────────────────── */
function GraveDetail({ grave, onClose, onReserve, plotNumber }: {
  grave: GraveEntry | null;
  onClose: () => void;
  onReserve: (id: string) => void;
  plotNumber: number;
}) {
  const styleInfo = grave ? GRAVE_STYLES.find(s => s.id === grave.style) : null;
  return (
    <AnimatePresence>
      {grave && (
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 40 }}
          transition={springPresets.gentle}
          className="fixed right-6 top-1/2 -translate-y-1/2 z-50 w-72 rounded-2xl overflow-hidden"
          style={{ background: 'oklch(0.10 0.025 215)', border: '1px solid oklch(0.22 0.04 45)', boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 40px oklch(0.72 0.14 45 / 0.08)' }}
        >
          <div className="relative h-36 flex items-center justify-center overflow-hidden"
            style={{ background: 'linear-gradient(135deg, oklch(0.13 0.04 220), oklch(0.08 0.02 230))' }}>
            <div className="absolute bottom-0 left-0 right-0 h-10 opacity-20"
              style={{ background: 'linear-gradient(to top, oklch(0.14 0.06 155), transparent)' }} />
            <div className="relative" style={{ transform: 'scale(1.6)', transformOrigin: 'center bottom' }}>
              {grave.name ? (
                <div style={{ width: 52, height: 76 }}>
                  <GraveShape style={grave.style} plan={grave.plan} highlight={true} />
                </div>
              ) : (
                <div className="w-14 h-20 rounded-sm flex items-center justify-center"
                  style={{ border: '1.5px dashed oklch(0.35 0.04 220)', background: 'oklch(0.09 0.02 230)' }}>
                  <span className="text-primary opacity-40 text-xl">+</span>
                </div>
              )}
            </div>
            {styleInfo && grave.name && (
              <div className="absolute top-3 left-4">
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: 'oklch(0.14 0.03 220)', color: 'rgba(170,185,205,0.7)', border: '1px solid oklch(0.22 0.03 220)' }}>
                  {styleInfo.label}
                </span>
              </div>
            )}
            <div className="absolute top-3 right-4">
              <span className="text-[9px] font-medium uppercase tracking-wider"
                style={{ color: PLAN_COLORS[grave.plan] }}>
                {grave.plan === "premium" ? "⭐ Premium" : grave.plan === "podstawowy" ? "● Podstawowy" : "◦ Free"}
              </span>
            </div>
            {/* Numer miejsca */}
            <div className="absolute bottom-2 left-4 text-[9px] font-mono opacity-50"
              style={{ color: 'rgba(180,195,215,0.8)' }}>
              Miejsce #{plotNumber} · {ALLEY_NAMES[grave.row]}
            </div>
          </div>

          <div className="px-5 pt-4 pb-5">
            <h3 className="text-lg font-semibold mb-0.5"
              style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(235,230,215,0.95)' }}>
              {grave.name || "Wolne miejsce"}
            </h3>
            {grave.years && <p className="text-sm mb-3" style={{ color: 'rgba(155,170,190,0.7)' }}>{grave.years}</p>}
            {grave.epitaph && (
              <p className="text-sm italic mb-4 leading-relaxed"
                style={{ color: 'rgba(185,200,218,0.75)', fontFamily: "'Playfair Display', serif" }}>
                „{grave.epitaph}"
              </p>
            )}
            {grave.name ? (
              <>
                {grave.lit && (
                  <div className="flex items-center gap-2 mb-4 text-xs" style={{ color: 'rgba(140,155,175,0.65)' }}>
                    <Flame className="w-3.5 h-3.5" style={{ color: 'oklch(0.80 0.18 60)' }} />
                    <span>Zapalona świeczka</span>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <button className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 hover:brightness-110"
                    style={{ background: PLAN_COLORS[grave.plan], color: 'oklch(0.08 0.01 240)' }}>
                    Odwiedź
                  </button>
                  <button className="px-3 py-2.5 rounded-lg text-sm transition-all duration-200 hover:bg-white/5"
                    style={{ border: '1px solid oklch(0.25 0.03 220)', color: 'rgba(170,185,205,0.8)' }}
                    onClick={onClose}>✕</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm mb-4" style={{ color: 'rgba(160,175,195,0.7)' }}>
                  To miejsce jest wolne. Zarezerwuj je i stwórz swój e-grób.
                </p>
                <button
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200 hover:brightness-110 flex items-center justify-center gap-2"
                  style={{ background: 'oklch(0.72 0.14 45)', color: 'oklch(0.08 0.01 240)' }}
                  onClick={() => onReserve(grave.id)}
                >
                  Zarezerwuj to miejsce <ChevronRight className="w-4 h-4" />
                </button>
                <button className="w-full mt-2 py-2 text-xs transition-opacity hover:opacity-60"
                  style={{ color: 'rgba(140,155,175,0.6)' }}
                  onClick={onClose}>Zamknij</button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Modal wyboru stylu ────────────────────────────────── */
function StylePickerModal({ plotId, onClose, onConfirm }: {
  plotId: string;
  onClose: () => void;
  onConfirm: (style: GraveStyle, custom: boolean) => void;
}) {
  const [picked, setPicked] = useState<GraveStyle>("classic");
  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(4,6,14,0.82)' }} />
      <motion.div className="relative w-full max-w-2xl rounded-2xl overflow-hidden z-10"
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        transition={springPresets.gentle} onClick={e => e.stopPropagation()}
        style={{ background: 'oklch(0.10 0.025 215)', border: '1px solid oklch(0.22 0.04 45)', boxShadow: '0 32px 80px rgba(0,0,0,0.8)' }}
      >
        <div className="px-7 pt-7 pb-5 border-b" style={{ borderColor: 'oklch(0.16 0.03 220)' }}>
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(140,155,175,0.6)' }}>
            Krok 1 z 2 — Miejsce #{plotId}
          </p>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(235,225,200,0.95)' }}>
            Wybierz styl nagrobka
          </h2>
          <p className="text-sm mt-1.5" style={{ color: 'rgba(145,160,180,0.7)' }}>
            Możesz wybrać gotowy szablon lub zaprojektować własny w kreatorze.
          </p>
        </div>
        <div className="px-7 py-5 grid grid-cols-3 sm:grid-cols-5 gap-3 max-h-80 overflow-y-auto">
          {GRAVE_STYLES.map(s => {
            const isActive = picked === s.id;
            return (
              <button key={s.id} onClick={() => setPicked(s.id)}
                className="flex flex-col items-center gap-2 p-2.5 rounded-xl transition-all duration-200 relative"
                style={{ background: isActive ? 'oklch(0.14 0.04 220)' : 'oklch(0.08 0.015 225)', border: `1.5px solid ${isActive ? 'oklch(0.72 0.14 45)' : 'oklch(0.18 0.025 220)'}`, boxShadow: isActive ? '0 0 16px oklch(0.72 0.14 45 / 0.2)' : 'none' }}>
                {s.premium && (
                  <div className="absolute top-1.5 right-1.5 w-3 h-3 flex items-center justify-center" style={{ color: 'oklch(0.72 0.14 45)' }}>
                    <Star className="w-2.5 h-2.5" />
                  </div>
                )}
                <div className="w-9 h-12 flex items-end justify-center">
                  <GraveShape style={s.id} plan="premium" highlight={isActive} />
                </div>
                <span className="text-[9px] text-center font-medium leading-tight"
                  style={{ color: isActive ? 'rgba(212,175,55,0.9)' : 'rgba(165,180,200,0.7)' }}>
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>
        <div className="px-7 pb-2 pt-1">
          {GRAVE_STYLES.find(s => s.id === picked) && (
            <p className="text-xs" style={{ color: 'rgba(130,145,165,0.65)' }}>
              <span style={{ color: 'rgba(212,175,55,0.75)' }}>{GRAVE_STYLES.find(s => s.id === picked)?.label}</span>
              {" — "}{GRAVE_STYLES.find(s => s.id === picked)?.desc}
              {GRAVE_STYLES.find(s => s.id === picked)?.premium && (
                <span style={{ color: 'oklch(0.72 0.14 45)' }}> · Plan Premium lub Podstawowy</span>
              )}
            </p>
          )}
        </div>
        <div className="px-7 pb-7 pt-4 flex gap-3">
          <button className="flex-1 py-3 rounded-xl font-medium transition-all duration-200 hover:brightness-110 flex items-center justify-center gap-2"
            style={{ background: 'oklch(0.72 0.14 45)', color: 'oklch(0.07 0.01 240)', fontSize: '0.875rem' }}
            onClick={() => onConfirm(picked, false)}>
            Użyj tego stylu <ChevronRight className="w-4 h-4" />
          </button>
          <button className="flex-1 py-3 rounded-xl font-medium transition-all duration-200 hover:bg-white/5 flex items-center justify-center gap-2"
            style={{ border: '1px solid oklch(0.25 0.04 220)', color: 'rgba(180,195,215,0.85)', fontSize: '0.875rem' }}
            onClick={() => onConfirm(picked, true)}>
            Projektuj własny <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Główna strona ─────────────────────────────────────── */
const ROWS_PER_PAGE = 5;
const TOTAL_ROWS = 20;

export default function Cemetery() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<GraveEntry | null>(null);
  const [selectedPlotNumber, setSelectedPlotNumber] = useState(0);
  const [filter, setFilter] = useState<"all" | "free" | "podstawowy" | "premium">("all");
  const [styleModal, setStyleModal] = useState<string | null>(null);

  // Obsługa ?page= i ?highlight= z panelu konta
  const initPage = Math.max(0, Math.min(3, parseInt(searchParams.get("page") ?? "0") || 0));
  const highlightParam = searchParams.get("highlight"); // format: "row-col"
  const [page, setPage] = useState(initPage);

  // Ref do podświetlonego miejsca
  const highlightRef = useRef<HTMLDivElement | null>(null);

  // Podświetlenie z URL
  const [urlHighlight, setUrlHighlight] = useState<string | null>(highlightParam);

  // Przewiń do podświetlonego miejsca po załadowaniu
  useEffect(() => {
    if (!urlHighlight) return;
    const timer = setTimeout(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 400);
    // Wyczyść podświetlenie po 4s
    const clearTimer = setTimeout(() => setUrlHighlight(null), 4000);
    return () => { clearTimeout(timer); clearTimeout(clearTimer); };
  }, [urlHighlight]);

  /* ── Wczytaj opublikowane groby z Supabase ── */
  const [dbGraves, setDbGraves] = useState<GraveEntry[]>([]);

  const loadDbGraves = useCallback(async () => {
    const { data } = await supabase
      .from("graves")
      .select("*")
      .eq("is_published", true);
    if (!data) return;
    const mapped: GraveEntry[] = data.map((g) => ({
      id:      `db-${g.id}`,
      name:    `${g.first_name} ${g.last_name}`.trim(),
      years:   g.birth_year && g.death_year ? `${g.birth_year}–${g.death_year}` : g.birth_year || g.death_year || "",
      epitaph: g.epitaph || "",
      plan:    (g.plan as "free" | "podstawowy" | "premium") || "free",
      lit:     false,
      row:     g.plot_row,
      col:     g.plot_col,
      style:   (g.theme_id as GraveStyle) in Object.fromEntries(GRAVE_STYLES.map(s => [s.id, true]))
               ? (g.theme_id as GraveStyle)
               : "classic",
    }));
    setDbGraves(mapped);
  }, []);

  useEffect(() => { loadDbGraves(); }, [loadDbGraves]);

  /* ── Merge: OCCUPIED + groby z DB (DB ma priorytet po kluczu row-col) ── */
  const mergedGraves = useMemo(() => {
    const dbByPos = new Map(dbGraves.map(g => [`${g.row}-${g.col}`, g]));
    const result: GraveEntry[] = [];
    for (let row = 0; row < 20; row++) {
      for (let col = 0; col < 5; col++) {
        const key = `${row}-${col}`;
        if (dbByPos.has(key)) {
          result.push(dbByPos.get(key)!);
        } else {
          const found = OCCUPIED.find(g => g.row === row && g.col === col);
          result.push(found ?? {
            id: `empty-${row}-${col}`,
            name: "", years: "", epitaph: "",
            plan: "free", lit: false,
            row, col, style: "classic",
          });
        }
      }
    }
    return result;
  }, [dbGraves]);

  const totalPages = Math.ceil(TOTAL_ROWS / ROWS_PER_PAGE);

  const handleReserve = (id: string) => {
    setSelected(null);
    setStyleModal(id);
  };

  const handleStyleConfirm = (style: GraveStyle, custom: boolean) => {
    const plotId = styleModal;
    setStyleModal(null);
    if (custom) {
      navigate(`${ROUTE_PATHS.REZERWACJA}?plot=${plotId}&style=${style}&custom=1`);
    } else {
      navigate(`${ROUTE_PATHS.REZERWACJA}?plot=${plotId}&style=${style}`);
    }
  };

  // Numer miejsca globalnie (1–100)
  const getPlotNumber = (row: number, col: number) => row * 5 + col + 1;

  // Czy dane miejsce jest podświetlone z URL
  const isUrlHighlighted = (row: number, col: number) => urlHighlight === `${row}-${col}`;

  // Rzędy aktualnej strony
  const visibleRows = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    const result: { row: number; graves: GraveEntry[] }[] = [];
    for (let r = start; r < end && r < TOTAL_ROWS; r++) {
      result.push({
        row: r,
        graves: mergedGraves.filter(g => g.row === r).sort((a, b) => a.col - b.col),
      });
    }
    return result;
  }, [page, mergedGraves]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return mergedGraves.filter(g => {
      if (!g.name) return false;
      const matchQ = !q || g.name.toLowerCase().includes(q) || g.epitaph.toLowerCase().includes(q);
      const matchF = filter === "all" || g.plan === filter;
      return matchQ && matchF;
    });
  }, [query, filter, mergedGraves]);

  const highlightIds = useMemo(() => new Set(filtered.map(g => g.id)), [filtered]);

  const handleGraveClick = (g: GraveEntry) => {
    if (!g.name) { setStyleModal(g.id); return; }
    const pn = getPlotNumber(g.row, g.col);
    setSelectedPlotNumber(pn);
    setSelected(prev => prev?.id === g.id ? null : g);
  };

  const isSearching = query.trim().length > 0;

  return (
    <Layout>
      <div className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(180deg, oklch(0.09 0.015 235) 0%, oklch(0.11 0.025 225) 40%, oklch(0.10 0.02 230) 100%)' }}>

        <div className="pointer-events-none fixed top-0 left-0 right-0 h-48 z-10"
          style={{ background: 'linear-gradient(to bottom, oklch(0.09 0.015 235), transparent)' }} />

        {/* Nagłówek */}
        <div className="container mx-auto px-4 max-w-5xl pt-32 pb-8 relative z-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={springPresets.gentle}
            className="text-center mb-10">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-3"
              style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(212,175,55,0.75)', textShadow: '0 0 40px rgba(212,175,55,0.15)' }}>
              Plac cyfrowy
            </h1>
            <p className="text-sm uppercase tracking-[0.3em] mb-2" style={{ color: 'rgba(140,155,175,0.7)' }}>
              e-grób — przestrzeń wieczności
            </p>
          <div className="flex justify-center gap-4 mt-3">
              <Badge variant="outline" className="border-primary/20 text-xs" style={{ color: 'rgba(180,195,215,0.6)' }}>
                {mergedGraves.filter(g => g.name).length} zajętych
              </Badge>
              <Badge variant="outline" className="border-primary/20 text-xs" style={{ color: 'rgba(180,195,215,0.6)' }}>
                {mergedGraves.filter(g => !g.name).length} wolnych
              </Badge>
              <Badge variant="outline" className="border-primary/20 text-xs" style={{ color: 'rgba(180,195,215,0.6)' }}>
                100 miejsc łącznie
              </Badge>
            </div>
          </motion.div>

          {/* Wyszukiwarka + filtry */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.15 }} className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                style={{ color: 'rgba(140,155,175,0.5)' }} />
              <Input
                placeholder="Znajdź grób — wpisz imię lub nazwisko..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="pl-11 h-12 text-sm rounded-xl border-0 focus-visible:ring-1 focus-visible:ring-primary/60"
                style={{ background: 'oklch(0.10 0.025 215)', color: 'rgba(200,215,230,0.9)', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}
              />
            </div>
            <div className="flex gap-2">
              {(["all","free","podstawowy","premium"] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className="px-3 h-12 rounded-xl text-xs font-medium capitalize transition-all duration-200"
                  style={{
                    background: filter === f ? PLAN_COLORS[f === "all" ? "premium" : f] : 'oklch(0.10 0.025 215)',
                    color: filter === f ? 'oklch(0.08 0.01 240)' : 'rgba(160,175,195,0.7)',
                    border: `1px solid ${filter === f ? 'transparent' : 'oklch(0.18 0.025 220)'}`,
                  }}>
                  {f === "all" ? "Wszystkie" : f === "free" ? "Free" : f === "podstawowy" ? "Podst." : "Premium"}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Wyniki wyszukiwania */}
          {isSearching && (
            <div className="mb-4 text-sm" style={{ color: 'rgba(150,165,185,0.6)' }}>
              Znaleziono: <span style={{ color: 'rgba(185,200,215,0.8)' }}>{filtered.length}</span> grobów
            </div>
          )}
        </div>

        {/* ── Plac ── */}
        <div className="flex-1 relative overflow-hidden">
          <div className="absolute inset-0"
            style={{ background: `radial-gradient(ellipse at 20% 60%, oklch(0.10 0.04 150 / 0.3) 0%, transparent 50%), oklch(0.07 0.02 150)` }} />
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'repeating-linear-gradient(90deg, oklch(0.15 0.05 150) 0px, transparent 1px, transparent 28px)', backgroundSize: '28px 100%' }} />

          <div className="relative z-20 container mx-auto px-4 max-w-5xl pt-4 pb-12">

            {/* ── Widok wyszukiwania – wszystkie pasujące rzędy ── */}
            {isSearching ? (
              <div className="space-y-12">
                {Array.from(new Set(filtered.map(g => g.row))).sort((a,b) => a-b).map(rowIdx => {
                  const rowGraves = mergedGraves.filter(g => g.row === rowIdx).sort((a,b) => a.col - b.col);
                  return (
                    <motion.div key={rowIdx} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={springPresets.gentle}>
                      <AlleyHeader row={rowIdx} />
                      <div className="flex justify-center items-end gap-8 sm:gap-12 flex-wrap mt-6">
                        {rowGraves.map(grave => {
                          const pn = getPlotNumber(grave.row, grave.col);
                          const isHL = highlightIds.has(grave.id);
                          const isDimmed = !isHL && grave.name;
                          return (
                            <motion.div key={grave.id} animate={{ opacity: isDimmed ? 0.2 : 1 }} transition={{ duration: 0.3 }}>
                              {grave.id === "g1" ? (
                                <InactiveGraveWrapper lastLogin={LAST_LOGIN_DATE}>
                                  <GraveCard grave={grave} onClick={handleGraveClick}
                                    highlight={isHL || selected?.id === grave.id} plotNumber={pn} />
                                </InactiveGraveWrapper>
                              ) : (
                                <GraveCard grave={grave} onClick={handleGraveClick}
                                  highlight={isHL || selected?.id === grave.id} plotNumber={pn} />
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                      <RowDivider />
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              /* ── Widok stronicowany ── */
              <>
                <AnimatePresence mode="wait">
                  <motion.div key={page} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3, ease: [0.22,1,0.36,1] }}
                    className="space-y-12">
                    {visibleRows.map(({ row, graves: rowGraves }) => (
                      <div key={row}>
                        <AlleyHeader row={row} />
                        <div className="flex justify-center items-end gap-8 sm:gap-12 flex-wrap mt-6">
                          {rowGraves.map(grave => {
                            const pn = getPlotNumber(grave.row, grave.col);
                            const isUrlHL = isUrlHighlighted(grave.row, grave.col);
                            const inner = grave.id === "g1" ? (
                              <InactiveGraveWrapper key={grave.id} lastLogin={LAST_LOGIN_DATE}>
                                <GraveCard grave={grave} onClick={handleGraveClick}
                                  highlight={selected?.id === grave.id || isUrlHL} plotNumber={pn} />
                              </InactiveGraveWrapper>
                            ) : (
                              <GraveCard key={grave.id} grave={grave} onClick={handleGraveClick}
                                highlight={selected?.id === grave.id || isUrlHL} plotNumber={pn} />
                            );
                            return (
                              <div
                                key={grave.id}
                                ref={isUrlHL ? highlightRef : undefined}
                                className="relative"
                              >
                                {isUrlHL && (
                                  <motion.div
                                    animate={{ opacity: [0, 0.7, 0, 0.7, 0] }}
                                    transition={{ duration: 2.5, repeat: 1 }}
                                    className="absolute -inset-4 rounded-2xl pointer-events-none z-0"
                                    style={{ background: 'radial-gradient(ellipse, rgba(212,175,55,0.18) 0%, transparent 70%)', border: '1px solid rgba(212,175,55,0.35)' }}
                                  />
                                )}
                                {inner}
                              </div>
                            );
                          })}
                        </div>
                        <RowDivider />
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>

                {/* ── Paginacja ── */}
                <div className="flex items-center justify-center gap-3 mt-12">
                  <button
                    onClick={() => { setPage(p => Math.max(0, p - 1)); setSelected(null); }}
                    disabled={page === 0}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 disabled:opacity-25 disabled:cursor-not-allowed"
                    style={{ background: 'oklch(0.10 0.025 215)', border: '1px solid oklch(0.20 0.03 220)', color: 'rgba(180,195,215,0.8)' }}
                  >
                    <ChevronLeft className="w-4 h-4" /> Poprzednie aleje
                  </button>

                  <div className="flex gap-1.5">
                    {Array.from({ length: totalPages }, (_, i) => {
                      const startRow = i * ROWS_PER_PAGE;
                      const endRow = Math.min(startRow + ROWS_PER_PAGE - 1, TOTAL_ROWS - 1);
                      return (
                        <button key={i} onClick={() => { setPage(i); setSelected(null); }}
                          className="flex flex-col items-center px-3 py-2 rounded-xl text-xs transition-all duration-200"
                          style={{
                            background: page === i ? 'oklch(0.72 0.14 45)' : 'oklch(0.10 0.025 215)',
                            color: page === i ? 'oklch(0.08 0.01 240)' : 'rgba(160,175,195,0.7)',
                            border: `1px solid ${page === i ? 'transparent' : 'oklch(0.18 0.025 220)'}`,
                            minWidth: 52,
                          }}>
                          <span className="font-medium">{i + 1}</span>
                          <span className="opacity-70" style={{ fontSize: 9 }}>Al. {startRow + 1}–{endRow + 1}</span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => { setPage(p => Math.min(totalPages - 1, p + 1)); setSelected(null); }}
                    disabled={page === totalPages - 1}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 disabled:opacity-25 disabled:cursor-not-allowed"
                    style={{ background: 'oklch(0.10 0.025 215)', border: '1px solid oklch(0.20 0.03 220)', color: 'rgba(180,195,215,0.8)' }}
                  >
                    Następne aleje <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Informacja o bieżącej stronie */}
                <p className="text-center mt-3 text-xs" style={{ color: 'rgba(120,135,155,0.5)' }}>
                  Aleje {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, TOTAL_ROWS)} z {TOTAL_ROWS}
                </p>
              </>
            )}

            {/* Legenda */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="flex justify-center gap-5 mt-10 flex-wrap">
              {[
                { color: PLAN_COLORS.free,       label: "Free" },
                { color: PLAN_COLORS.podstawowy, label: "Podstawowy" },
                { color: PLAN_COLORS.premium,    label: "Premium" },
                { color: 'oklch(0.80 0.18 60)',  label: "Zapalona świeczka" },
                { color: 'oklch(0.30 0.03 220)', label: "Wolne miejsce" },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                  <span className="text-xs" style={{ color: 'rgba(140,155,175,0.6)' }}>{label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      <GraveDetail grave={selected} onClose={() => setSelected(null)} onReserve={handleReserve} plotNumber={selectedPlotNumber} />

      <AnimatePresence>
        {styleModal && (
          <StylePickerModal
            plotId={styleModal}
            onClose={() => setStyleModal(null)}
            onConfirm={handleStyleConfirm}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}

/* ── Pomocniki wizualne ── */
function AlleyHeader({ row }: { row: number }) {
  const alleyName = ALLEY_NAMES[row] ?? `Aleja ${row + 1}`;
  const startPlot = row * 5 + 1;
  const endPlot   = row * 5 + 5;
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 opacity-15"
        style={{ background: 'linear-gradient(to right, transparent, oklch(0.72 0.14 45))' }} />
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-xs font-medium tracking-wide" style={{ color: 'rgba(212,175,55,0.55)', fontFamily: "'Playfair Display', serif" }}>
          {alleyName}
        </span>
        <span className="text-[9px] font-mono opacity-35" style={{ color: 'rgba(160,175,200,0.7)' }}>
          miejsca #{startPlot}–#{endPlot}
        </span>
      </div>
      <div className="h-px flex-1 opacity-15"
        style={{ background: 'linear-gradient(to left, transparent, oklch(0.72 0.14 45))' }} />
    </div>
  );
}

function RowDivider() {
  return (
    <div className="mx-auto mt-6 h-px rounded-full opacity-10"
      style={{ maxWidth: 600, background: 'linear-gradient(to right, transparent, oklch(0.35 0.02 200), transparent)' }} />
  );
}
